import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSalesReport } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, DollarSign, CalendarDays, Clock, Award, Users } from "lucide-react";

function PctChange({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  const pct = previous === 0 ? 100 : ((current - previous) / previous) * 100;
  const isUp = pct >= 0;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${isUp ? "text-green-600" : "text-red-600"}`}>
      {isUp ? <TrendingUp className="size-3 mr-0.5" /> : <TrendingDown className="size-3 mr-0.5" />}
      {isUp ? "+" : ""}{pct.toFixed(0)}%
    </span>
  );
}

type TabKey = "overview" | "top_items" | "hourly" | "tables" | "daily";

export function SalesPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();
  const [period, setPeriod] = useState("daily");
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const { data, isLoading } = useQuery({
    queryKey: ["sales", venueId, period],
    queryFn: () => getSalesReport(venueId!, period),
    enabled: !!venueId,
  });

  const maxHourlyRevenue = data?.hourlyActivity ? Math.max(...data.hourlyActivity.map((h) => h.revenue), 1) : 1;

  const tabs: { key: TabKey; icon: typeof DollarSign; labelKey: string; value?: string }[] = [
    { key: "overview", icon: DollarSign, labelKey: "total_revenue", value: data ? `${data.totalRevenue.toFixed(0)} ₺` : undefined },
    { key: "top_items", icon: Award, labelKey: "top_selling_items", value: data?.topItems ? `${data.topItems.length}` : undefined },
    { key: "hourly", icon: Clock, labelKey: "hourly_activity", value: data ? `${data.hourlyActivity.filter(h => h.revenue > 0).length}h` : undefined },
    { key: "tables", icon: Users, labelKey: "table_performance", value: data?.tablePerformance ? `${data.tablePerformance.length}` : undefined },
    { key: "daily", icon: CalendarDays, labelKey: "daily_breakdown", value: data?.dailyBreakdown ? `${data.dailyBreakdown.length}` : undefined },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("sales")}</h1>
        <div className="flex gap-2">
          {(["daily", "weekly", "monthly"] as const).map((p) => (
            <Badge key={p} variant={period === p ? "default" : "outline"} className="cursor-pointer" onClick={() => setPeriod(p)}>
              {t(`sales_${p}` as Parameters<typeof t>[0])}
            </Badge>
          ))}
        </div>
      </div>

      {/* Tab cards */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 mb-6">
        {tabs.map(({ key, icon: Icon, labelKey, value }) => (
          <Card
            key={key}
            className={`cursor-pointer transition-all ${activeTab === key ? "border-primary bg-primary/5 shadow-sm" : "hover:border-muted-foreground/30"}`}
            onClick={() => setActiveTab(key)}
          >
            <CardContent className="pt-3 pb-3 text-center">
              <Icon className={`size-5 mx-auto mb-1 ${activeTab === key ? "text-primary" : "text-muted-foreground"}`} />
              <p className="text-xs font-medium text-muted-foreground">{t(labelKey as Parameters<typeof t>[0])}</p>
              {value && <p className={`text-lg font-bold mt-0.5 ${activeTab === key ? "text-primary" : ""}`}>{isLoading ? "..." : value}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <p className="text-center text-muted-foreground py-8">{t("loading")}</p>
      ) : (
        <>
          {/* Overview */}
          {activeTab === "overview" && data && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">{t("total_revenue")}</p>
                    <p className="text-2xl font-bold">{data.totalRevenue.toFixed(0)} ₺</p>
                    {data.previousPeriod && <PctChange current={data.totalRevenue} previous={data.previousPeriod.totalRevenue} />}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">{t("total_orders")}</p>
                    <p className="text-2xl font-bold">{data.totalOrders}</p>
                    {data.previousPeriod && <PctChange current={data.totalOrders} previous={data.previousPeriod.totalOrders} />}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">{t("closed_bills")}</p>
                    <p className="text-2xl font-bold">{data.totalBills}</p>
                    {data.previousPeriod && <PctChange current={data.totalBills} previous={data.previousPeriod.totalBills} />}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground">{t("avg_bill_amount")}</p>
                    <p className="text-2xl font-bold">{(data.avgBillAmount ?? 0).toFixed(0)} ₺</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Top selling items */}
          {activeTab === "top_items" && data?.topItems && (
            <div className="space-y-2">
              {data.topItems.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t("no_data")}</p>
              ) : data.topItems.map((item, i) => (
                <Card key={item.name}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.quantity} {t("sold_count")}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-primary">{item.revenue.toFixed(0)} ₺</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Hourly activity */}
          {activeTab === "hourly" && data?.hourlyActivity && (
            <Card>
              <CardContent className="py-4">
                {data.hourlyActivity.filter(h => h.revenue > 0).length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">{t("no_data")}</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.hourlyActivity.filter(h => h.revenue > 0).map((h) => (
                      <div key={h.hour} className="flex items-center gap-2 text-sm">
                        <span className="w-12 text-right text-muted-foreground font-mono">{String(h.hour).padStart(2, "0")}:00</span>
                        <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                          <div className="h-full bg-primary/70 rounded flex items-center px-2" style={{ width: `${Math.max((h.revenue / maxHourlyRevenue) * 100, 8)}%` }}>
                            <span className="text-[10px] text-white font-medium truncate">{h.revenue.toFixed(0)} ₺</span>
                          </div>
                        </div>
                        <span className="w-12 text-right text-xs text-muted-foreground">{h.bills} {t("closed_bills").toLowerCase().split(" ")[0]}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Table performance */}
          {activeTab === "tables" && data?.tablePerformance && (
            <div className="space-y-2">
              {data.tablePerformance.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t("no_data")}</p>
              ) : data.tablePerformance.map((tp, i) => (
                <Card key={tp.tableName}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex size-8 items-center justify-center rounded-full bg-muted text-sm font-bold">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold">{tp.tableName}</p>
                        <p className="text-xs text-muted-foreground">{tp.bills} {t("closed_bills").toLowerCase()}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-primary">{tp.revenue.toFixed(0)} ₺</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Daily breakdown */}
          {activeTab === "daily" && data?.dailyBreakdown && (
            <div className="space-y-2">
              {data.dailyBreakdown.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t("no_data")}</p>
              ) : data.dailyBreakdown.map((day) => (
                <Card key={day.date}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="size-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-semibold">{new Date(day.date).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</p>
                        <p className="text-xs text-muted-foreground">{day.bills} {t("closed_bills").toLowerCase()} · {day.orders} {t("orders").toLowerCase()}</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-primary">{day.revenue.toFixed(0)} ₺</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
